// src/components/dashboard/ExpertMatchCard.js

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { inviteHelperToProblem } from "../../services/problemService";
import { logger } from "../../utils/logger";

/**
 * A suggested expert, with the reasoning shown.
 *
 * This replaced a card whose headline was a green "87% Match" pill with nothing
 * behind it. The customer is being asked to hand a production outage to a
 * stranger, so the card now leads with what the expert covers, whether their
 * hours overlap, and what they have actually closed.
 *
 * It also no longer calls calculateHelperReputation on mount. That helper reads
 * every incident the expert has ever had and then every message inside each one,
 * which meant rendering three suggestions could fan out into dozens of queries.
 * The same completion rate now arrives pre-computed with the ranking.
 */

function initialsOf(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function ExpertMatchCard({
  helper,
  isStrongMatch,
  reasons = [],
  problemId,
}) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [inviting, setInviting] = useState(false);

  const helperId = helper.uid || helper.id;

  const handleInvite = async () => {
    if (!problemId || !currentUser) {
      toast.error("Sign in to request a specialist.");
      return;
    }
    if (!helperId) {
      toast.error("This specialist's profile is incomplete.");
      return;
    }
    if (inviting) return;

    setInviting(true);
    try {
      logger.log("Inviting expert to incident:", { helperId, problemId });
      const result = await inviteHelperToProblem(
        problemId,
        currentUser.uid,
        helperId
      );
      if (result.success) {
        toast.success(
          `Request sent to ${helper.name || "the specialist"}. They will be notified now.`
        );
      } else {
        toast.error(result.error || "Could not send the request.");
      }
    } catch (error) {
      console.error("Error inviting expert:", error);
      toast.error(`Could not send the request: ${error.message}`);
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
          {initialsOf(helper.name)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-sm font-semibold text-slate-900">
              {helper.name || "Specialist"}
            </p>
            {isStrongMatch && (
              <span className="flex-shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                Strong match
              </span>
            )}
          </div>
          {helper.tagline && (
            <p className="truncate text-xs text-slate-500">{helper.tagline}</p>
          )}
        </div>
      </div>

      {reasons.length > 0 && (
        <ul className="mt-3 space-y-1">
          {reasons.map((reason) => (
            <li key={reason} className="flex gap-2 text-xs text-slate-600">
              <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-slate-300" />
              {reason}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(`/user/${helperId}`)}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          View profile
        </button>
        <button
          type="button"
          onClick={handleInvite}
          disabled={inviting}
          className="flex-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {inviting ? "Sending…" : "Request expert"}
        </button>
      </div>
    </div>
  );
}
